import React, { Component } from 'react';
import { getAmplitudeInstance } from '@alltrails/shared/utils/amplitudeHelpers';
import NoSsr from '@material-ui/core/NoSsr';
import { Clear } from '@material-ui/icons';
import PropTypes from 'prop-types';
import * as styles from './styles/styles.module.scss';
import CloudCheckbox from '../CloudCheckbox';
import DatePicker from '../../shared/DatePicker';
import FormButton from '../FormButton';
import Modal from '../../shared/Modal';
import SelectDropdown from '../SelectDropdown';
import StarRating from '../../shared/StarRating';
import TrailFormSuccess from '../../shared/TrailFormSuccess';
import { activities, obstacles, defaultHikingActivity, getActivityByValue } from '../../../utils/activity_util';
import { monthDayYear } from '../../../utils/time_helpers';
import { ServerCommunicationUtil } from '../../../utils/server_communication_util';
import { StringConstantsMixin } from '../../../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import { REVIEW_FORM_VIEWED, REVIEW_UPDATED } from '../../../utils/constants/AmplitudeEvent';

const FIRST_STEP = 1;
const LAST_STEP = 2;

/**
 * logAnalyticsOnView is meant to log Amplitude analytics once when the component is rendered
 *
 * @param {number} reviewId
 * @param {string} comment
 * @param {number} rating
 * @param {string} activityUid
 * @param {string} conditions
 * @param {string} variant
 */
const logAnalyticsOnView = (reviewId, comment, rating, activityUid, conditions, variant) => {
  const analyticsFields = {};

  if (reviewId) {
    analyticsFields['review id'] = reviewId;
  }
  if (comment) {
    analyticsFields['comment shown'] = comment.slice(0, 200);
  }
  if (rating) {
    analyticsFields['rating shown'] = rating;
  }
  if (activityUid) {
    analyticsFields['activity type shown'] = activityUid;
  }
  if (conditions && conditions.length > 0) {
    analyticsFields['conditions shown'] = JSON.stringify([...conditions].sort());
  }
  if (variant) {
    analyticsFields.variant = variant;
  }

  getAmplitudeInstance().logEvent(REVIEW_FORM_VIEWED, analyticsFields);
};

/**
 * logAnalyticsOnSave is meant to log Amplitude analytics after a successful review save
 *
 * @param {object} trailReview
 * @param {object} state
 * @param {string} variant
 */
const logAnalyticsOnSave = (trailReview, state, variant) => {
  const savedComment = trailReview.comment || '';
  const savedRating = trailReview.rating;
  const savedActivityType = trailReview.activity;
  const savedConditions = trailReview.obstacles.map(o => o.uid);

  const analyticsFields = {
    'review id': trailReview.id
  };

  if (savedComment !== state.originalComment) {
    analyticsFields.comment = savedComment.slice(0, 200);
    if (state.originalComment) {
      analyticsFields['original comment'] = state.originalComment.slice(0, 200);
    }
  }
  if (savedRating !== state.originalRating) {
    analyticsFields.rating = savedRating;
    if (state.originalRating) {
      analyticsFields['original rating'] = state.originalRating;
    }
  }
  if (savedActivityType.uid !== state.originalActivityType.value) {
    analyticsFields['activity type'] = savedActivityType.uid;
    if (state.originalActivityType) {
      analyticsFields['original activity type'] = state.originalActivityType.value;
    }
  }
  if (JSON.stringify([...savedConditions].sort()) !== JSON.stringify([...state.originalConditions].sort())) {
    analyticsFields.conditions = savedConditions;
    if (state.originalConditions) {
      analyticsFields['original conditions'] = state.originalConditions;
    }
  }
  if (variant) {
    analyticsFields.variant = variant;
  }

  getAmplitudeInstance().logEvent(REVIEW_UPDATED, analyticsFields);
};

class ReviewFormModal extends Component {
  constructor(props) {
    super(props);

    // It is possible for an activity to be set as null, which is distinct from
    // it being derived from the default value.
    const activity = props.activity || {};

    const comment = props.comment_source || props.comment;
    const rating = props.rating;
    const activityType = getActivityByValue(activity.uid) || defaultHikingActivity();
    const conditions = props.obstacles.map(obs => obs.uid);

    logAnalyticsOnView(this.props.id, comment, rating, activity.uid, conditions, props.reviewSource);

    this.state = {
      activity,

      // For analytics. Never updated.
      originalComment: comment,
      originalRating: rating,
      originalActivityType: { ...activityType },
      originalConditions: [...conditions],

      comment,
      rating,
      successfulSubmission: false,
      serverError: '',
      submitting: false,
      activeStep: FIRST_STEP, // total of two steps
      selectedActivity: activityType,
      date: props.date,
      activeObstacles: conditions
    };
  }

  addReview(trailReview) {
    const payload = { detail: trailReview };
    if (this.props.exploreFlag) {
      const event = new CustomEvent('addReview', payload);
      window.document.dispatchEvent(event);
      return;
    }
    this.props.onReviewAdd(payload);
  }

  updateReview(trailReview) {
    const payload = { detail: trailReview };
    if (this.props.exploreFlag) {
      const event = new CustomEvent('updateReview', payload);
      window.document.dispatchEvent(event);
      return;
    }

    this.props.updateReview(payload);
  }

  updateComment = e => {
    const { value } = e.currentTarget;

    this.setState({ comment: value });
  };

  setRating = (e, rating) => {
    e.preventDefault();
    this.setState({ rating });
  };

  addObstacle(uid) {
    this.setState({ activeObstacles: [...this.state.activeObstacles, uid] });
  }

  removeObstacle(uid) {
    const activeObstacles = this.state.activeObstacles.filter(obstacle => {
      return obstacle !== uid;
    });

    this.setState({ activeObstacles });
  }

  // gets
  getFormParams() {
    const { comment, rating, selectedActivity, date, activeObstacles } = this.state;

    return {
      comment,
      rating,
      activity_date: date,
      activity: selectedActivity.value,
      obstacle_uids: activeObstacles
    };
  }

  getFormButtonText() {
    if (!this.props.multistep) {
      return StringConstantsMixin.strings.REVIEW_UPLOAD_FORM.POST_REVIEW;
    }

    if (this.state.activeStep === FIRST_STEP) {
      return StringConstantsMixin.strings.REVIEW_UPLOAD_FORM.NEXT;
    }

    return StringConstantsMixin.strings.REVIEW_UPLOAD_FORM.POST;
  }

  // handlers
  handleSuccess = response => {
    const trailReview = response.trail_reviews[0];

    logAnalyticsOnSave(trailReview, this.state, this.props.reviewSource);

    if (this.props.id) {
      this.updateReview(trailReview);
    } else {
      this.addReview(trailReview);
    }

    this.setState({ successfulSubmission: true, submitting: false, serverError: '' });
  };

  handleError = err => {
    this.setState({ serverError: StringConstantsMixin.strings.PROGRESS_MESSAGES.ERROR_SAVING, submitting: false });
  };

  handleSelectChange = e => {
    const value = this.props.isMobile ? e.currentTarget.value : e.currentTarget.dataset.value;

    const selectedActivity = activities.find(activity => activity.value === value);
    this.setState({ selectedActivity });
  };

  handleDateChange = date => {
    this.setState({ date });
  };

  submitOrNextStep = e => {
    e.preventDefault();

    if (this.props.multistep && this.state.activeStep !== LAST_STEP) {
      return this.setState({ activeStep: LAST_STEP });
    }

    return this.handleSubmit();
  };

  handleCloudCheckboxChange = e => {
    const { value } = e.currentTarget;
    return this.state.activeObstacles.includes(value) ? this.removeObstacle(value) : this.addObstacle(value);
  };

  handleSubmit() {
    let url;
    this.setState({ submitting: true });
    const params = this.getFormParams();
    // if Trail Review record exists create
    if (this.props.id) {
      url = `/api/alltrails/users/${this.props.user.id}/reviews/${this.props.id}?key=${ServerCommunicationUtil.apiKey}`;
      ServerCommunicationUtil.putApiEndpoint(url, params, this.handleSuccess, this.handleError, () => {});
    } else {
      url = `/api/alltrails/trails/${this.props.trailId}/reviews?key=${ServerCommunicationUtil.apiKey}`;
      ServerCommunicationUtil.postApiEndpoint(url, params, this.handleSuccess, this.handleError, () => {});
    }
  }

  isActiveObstacle(obstacle) {
    return this.state.activeObstacles.includes(obstacle.uid);
  }

  backToFirstStep = () => {
    this.setState({ activeStep: FIRST_STEP });
  };

  renderCloudCheckboxes() {
    return obstacles.map(obstacle => {
      return (
        <CloudCheckbox
          key={obstacle.uid}
          label={obstacle.label}
          value={obstacle.uid}
          onChange={this.handleCloudCheckboxChange}
          checked={this.isActiveObstacle(obstacle)}
        />
      );
    });
  }

  renderFirstFormStep() {
    return (
      <>
        <div className={styles.ratingAndTextArea}>
          <div className={styles.starRatingContainer}>
            <StarRating config={this.state.rating} handler={this.setRating} iconClassName={styles.starRatingIconClass} />
          </div>
          <textarea
            placeholder={StringConstantsMixin.strings.REVIEW_UPLOAD_FORM.GIVE_BACK_TO_THE_COMMUNITY}
            className={styles.commentTextArea}
            value={this.state.comment}
            onChange={this.updateComment}
            name="comment"
          />
        </div>
      </>
    );
  }

  renderSecondFormStep() {
    return (
      <>
        <div className={styles.activityAndDate}>
          <div className={styles.sectionLabel}>{StringConstantsMixin.strings.ACTIVITY}*</div>
          <div className={styles.dropdowns}>
            <SelectDropdown
              options={activities}
              handleChange={this.handleSelectChange}
              defaultItem={this.state.selectedActivity}
              isMobile={this.props.isMobile}
            />
            <div className={styles.spacer} />
            <DatePicker date={this.state.date} onChange={this.handleDateChange} locale={this.props.locale} abbreviateMonth />
          </div>
        </div>
        <div className={styles.trailConditions}>
          <div className={`${styles.sectionLabel} ${styles.trailConditionLabel}`}>{StringConstantsMixin.strings.TRAIL_CONDITIONS}*</div>
          <div className={styles.cloudCheckboxesContainer}>{this.renderCloudCheckboxes()}</div>
        </div>
      </>
    );
  }

  renderActiveFormStep() {
    switch (this.state.activeStep) {
      case 1:
        return this.renderFirstFormStep();
      case 2:
        return this.renderSecondFormStep();
      default:
        return null;
    }
  }

  renderActiveFormContent() {
    if (!this.props.multistep) {
      return this.renderFirstFormStep();
    }

    return this.renderActiveFormStep();
  }

  renderForm() {
    return (
      <NoSsr>
        <form onSubmit={this.submitOrNextStep} className={styles.form}>
          {this.renderActiveFormContent()}
          {this.state.serverError.length > 0 && <div className={styles.serverError}>{this.state.serverError}</div>}
          <div className={styles.formButtonContainer}>
            {this.state.activeStep === LAST_STEP && (
              <button type="button" className={styles.cancelButton} onClick={this.backToFirstStep}>
                {StringConstantsMixin.strings.BACK}
              </button>
            )}
            <FormButton
              value={this.getFormButtonText()}
              disabled={this.state.rating === null || this.state.submitting}
              className={styles.formButton}
            />
          </div>
        </form>
      </NoSsr>
    );
  }

  renderStepsOrActivityAndDate() {
    const subText = this.props.multistep
      ? StringConstantsMixin.strings.REVIEW_UPLOAD_FORM.stepText(this.state.activeStep, LAST_STEP)
      : `${monthDayYear(this.props.date, this.props.locale)} ${'\u2022'} ${this.state.activity.name}`;

    return <div className={styles.dateAndActivityName}>{subText}</div>;
  }

  renderTitle() {
    return (
      <div className={styles.trailDataAndClear}>
        <div className={styles.trailName}>{this.props.trailName}</div>
        <div className={styles.dateAndActivityName}>{this.renderStepsOrActivityAndDate()}</div>
      </div>
    );
  }

  renderModalContent() {
    if (this.state.successfulSubmission) {
      return <TrailFormSuccess variant="review" />;
    }

    return (
      <>
        {this.renderTitle()}
        {this.renderForm()}
      </>
    );
  }

  render() {
    return (
      <Modal handleBackgroundClick={this.props.closeReviewModal}>
        <div className={styles.reviewFormContainer}>
          <div className={styles.clearContainer}>
            <button type="button" className={styles.clearIcon} onClick={this.props.closeReviewModal}>
              <Clear />
            </button>
          </div>
          {this.renderModalContent()}
        </div>
      </Modal>
    );
  }
}

ReviewFormModal.propTypes = {
  id: PropTypes.number,
  trailId: PropTypes.number.isRequired,
  trailName: PropTypes.string,
  user: PropTypes.shape({
    id: PropTypes.number
  }).isRequired,
  closeReviewModal: PropTypes.func.isRequired,
  date: PropTypes.string,
  comment: PropTypes.string,
  rating: PropTypes.number,
  obstacles: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      uid: PropTypes.string
    })
  ),
  activity: PropTypes.shape({
    name: PropTypes.string,
    type: PropTypes.string,
    uid: PropTypes.string
  }),
  multistep: PropTypes.bool,
  exploreFlag: PropTypes.bool,
  isMobile: PropTypes.bool,
  reviewSource: PropTypes.string
};

ReviewFormModal.defaultProps = {
  id: null,
  date: new Date(),
  rating: 1,
  comment: '',
  trailName: '',
  activity: {
    value: 'hiking'
  },
  obstacles: [],
  multistep: false,
  exploreFlag: false,
  isMobile: true,
  reviewSource: ''
};

export default ReviewFormModal;
